import type { Task as FhirTask } from "fhir/r5";

import { oids } from "@/configs/Settings";
import { fhirClients } from "src-next/shared/api/fhirClient";

import { crmContractService } from "../../contracts/services/crmContractService";
import type {
  CrmMessageBatchHistoryItem,
  CrmMessageUsageStats,
} from "../types";
import {
  buildCrmBatchHistoryFromTasks,
  computeCrmUtilizationRateInPercentage,
  countSentMessagesFromCrmTasks,
  countTotalContractedFromPaymentNotices,
  extractResourcesFromBundle,
  getAvailableMessagesFromContract,
  getRemainingMessagesForSendFromContract,
  pickFallbackCrmContractForEmptyBalance,
  resolveActiveCrmContractForSend,
} from "../helpers/crmMessageFranchiseStatsHelpers";

export const CRM_MESSAGE_FRANCHISE_EMPTY_STATS: CrmMessageUsageStats = {
  totalMessagesAvailable: 0,
  totalMessagesUsed: 0,
  totalMessagesContracted: 0,
  usageRateInPercentage: 0,
  remainingMessagesForSend: 0,
};

export interface CrmMessageFranchiseUsageData {
  stats: CrmMessageUsageStats;
  batchHistory: CrmMessageBatchHistoryItem[];
}

/**
 * Owner das Tasks CRM — paridade com legado `GetTask({ owner: organization.id })`.
 * Remove prefixo OID quando presente para bater com o índice FHIR usado pelo FluxZap.
 */
function buildOrganizationOwnerIdForTaskSearch(organizationId: string): string {
  const trimmed = organizationId.trim();
  const prefix = `${oids.cnpj}-`;
  if (trimmed.startsWith(prefix)) {
    return trimmed.slice(prefix.length);
  }
  return trimmed;
}

function buildUsageStats(params: {
  totalMessagesAvailable: number;
  totalMessagesUsed: number;
  remainingMessagesForSend: number;
  paymentNotices: fhir5.PaymentNotice[];
}): CrmMessageUsageStats {
  const {
    totalMessagesAvailable,
    totalMessagesUsed,
    remainingMessagesForSend,
    paymentNotices,
  } = params;
  return {
    totalMessagesAvailable,
    totalMessagesUsed,
    remainingMessagesForSend,
    totalMessagesContracted:
      countTotalContractedFromPaymentNotices(paymentNotices),
    usageRateInPercentage: computeCrmUtilizationRateInPercentage(
      totalMessagesAvailable,
      totalMessagesUsed,
    ),
  };
}

async function fetchPaymentNoticesForContract(
  contractId: string,
): Promise<fhir5.PaymentNotice[]> {
  try {
    const bundle = await fhirClients.admin.search<fhir5.PaymentNotice>(
      "PaymentNotice",
      {
        request: `Contract/${contractId}`,
        _sort: "-_lastUpdated",
      },
    );
    return extractResourcesFromBundle<fhir5.PaymentNotice>(bundle);
  } catch {
    return [];
  }
}

/**
 * Prioridade de saldo: crm20 → crm50/crm100.
 * Usado pelo gate do SendWhatsAppMessageDialog.
 */
async function fetchCrmMessageFranchiseUsageData(
  organizationId: string,
): Promise<CrmMessageFranchiseUsageData> {
  const trimmed = organizationId?.trim();
  if (!trimmed) {
    return {
      stats: { ...CRM_MESSAGE_FRANCHISE_EMPTY_STATS },
      batchHistory: [],
    };
  }

  const [trialContract, paidContract] = await Promise.all([
    crmContractService.getLatestCrm20ContractByOrganization({
      organizationId: trimmed,
    }),
    crmContractService.getLatestCrmPaidContractByOrganization({
      organizationId: trimmed,
    }),
  ]);

  const ownerId = buildOrganizationOwnerIdForTaskSearch(trimmed);

  let tasks: FhirTask[] = [];
  try {
    const tasksBundle = await fhirClients.workflow.search<FhirTask>("Task", {
      owner: `${oids.cnpj}-${ownerId}`,
      _sort: "-authored-on",
      _count: "1000",
    });
    tasks = extractResourcesFromBundle<FhirTask>(tasksBundle);
  } catch {
    tasks = [];
  }

  const totalMessagesUsed = countSentMessagesFromCrmTasks(tasks);
  const batchHistory = buildCrmBatchHistoryFromTasks(tasks);

  const activeContract = resolveActiveCrmContractForSend({
    trialContract,
    paidContract,
    totalMessagesUsed,
  });

  if (activeContract) {
    const paymentNotices = activeContract.contract.id
      ? await fetchPaymentNoticesForContract(activeContract.contract.id)
      : [];

    return {
      stats: buildUsageStats({
        totalMessagesAvailable: activeContract.totalMessagesAvailable,
        totalMessagesUsed: activeContract.totalMessagesUsed,
        remainingMessagesForSend: activeContract.remainingMessages,
        paymentNotices,
      }),
      batchHistory,
    };
  }

  const fallbackContract = pickFallbackCrmContractForEmptyBalance({
    trialContract,
    paidContract,
  });

  if (!fallbackContract?.id) {
    return {
      stats: {
        ...CRM_MESSAGE_FRANCHISE_EMPTY_STATS,
        totalMessagesUsed,
      },
      batchHistory,
    };
  }

  const totalMessagesAvailable =
    getAvailableMessagesFromContract(fallbackContract);
  const remainingMessagesForSend = getRemainingMessagesForSendFromContract(
    fallbackContract,
    totalMessagesUsed,
  );
  const paymentNotices = await fetchPaymentNoticesForContract(
    fallbackContract.id,
  );

  return {
    stats: buildUsageStats({
      totalMessagesAvailable,
      totalMessagesUsed,
      remainingMessagesForSend,
      paymentNotices,
    }),
    batchHistory,
  };
}

async function fetchCrmMessageFranchiseUsageStats(
  organizationId: string,
): Promise<CrmMessageUsageStats> {
  const result = await fetchCrmMessageFranchiseUsageData(organizationId);
  return result.stats;
}

export const crmMessageFranchiseStatsService = {
  fetchCrmMessageFranchiseUsageStats,
  fetchCrmMessageFranchiseUsageData,
};
